import { z } from 'zod';
import { CASE_DOCUMENT_CATEGORIES, type CaseDocumentCategory } from './documents';

// ───────────────────────────── Korespondencja z klientem i dokumenty klienta (docs/21) ─────────────────────────────

/** Kanał wiadomości: e-mail (skrzynka firmy) albo strefa klienta. Odpowiedź zawsze idzie tym samym kanałem. */
export const MESSAGE_CHANNELS = ['email', 'strefa'] as const;
export type MessageChannel = (typeof MESSAGE_CHANNELS)[number];
export const MESSAGE_CHANNEL_LABELS: Record<MessageChannel, string> = { email: 'E-mail', strefa: 'Strefa klienta' };

/** Limit treści wiadomości (znaki) i liczby załączników. */
export const MESSAGE_BODY_MAX = 20_000;
export const MESSAGE_ATTACHMENTS_MAX = 10;

export interface MessageAttachmentDto {
  id: string;
  fileName: string;
  size: number;
  mime: string;
  /** PDF i zdjęcia otwierane w przeglądarce, reszta pobierana */
  inline: boolean;
}

export interface PartyMessageDto {
  id: string;
  direction: 'in' | 'out';
  channel: MessageChannel;
  /** nadawca: adres e-mail klienta / „Klient (strefa)” / pracownik */
  fromLabel: string;
  /** e-mail: adres odbiorcy (wychodzące) albo nadawcy (przychodzące) */
  email: string | null;
  body: string;
  at: string;
  attachments: MessageAttachmentDto[];
  /** wychodzące e-maile: stan kolejki (oczekuje / wyslany / blad / blad_trwaly / do_sprawdzenia) */
  sendStatus: string | null;
  sendError: string | null;
  /** przychodzące: kto i kiedy pierwszy otworzył wątek */
  readAt: string | null;
}

export interface PartyThreadDto {
  id: string;
  partyId: string;
  channel: MessageChannel;
  subject: string;
  /** e-mail klienta, z którym toczy się wątek (kanał e-mail) */
  clientEmail: string | null;
  company: { id: string; shortName: string; color: string } | null;
  /** przypięta sprawa (wiadomości widać też w sprawie) */
  case: { id: string; number: string } | null;
  /** sprawa strefy klienta (kanał strefa) */
  portalCase: { id: string; number: string } | null;
  lastMessageAt: string;
  messageCount: number;
  /** przychodzące nieprzeczytane (nikt jeszcze nie otworzył) */
  unread: number;
  /** początek ostatniej wiadomości */
  snippet: string;
  lastDirection: 'in' | 'out';
  attachmentCount: number;
}

export interface PartyThreadDetailDto extends PartyThreadDto {
  messages: PartyMessageDto[];
  /** czy można odpowiedzieć tym kanałem (np. strefa wyłączona — nie) i dlaczego nie */
  canReply: boolean;
  replyBlocked: string | null;
}

export interface PartyCorrespondenceDto {
  threads: PartyThreadDto[];
  unread: number;
  /** możliwość napisania nowej wiadomości: firmy ze skrzynką / adresy klienta / strefa klienta (aktywna — sprawy klienta do wskazania) */
  compose: {
    canWrite: boolean;
    companies: { id: string; shortName: string }[];
    emails: string[];
    /** klient ma aktywną strefę klienta (wiadomość do strefy) */
    portalActive: boolean;
    /** sprawy klienta — opcjonalne wskazanie sprawy wiadomości w strefie */
    portals: { caseId: string; caseNumber: string }[];
  };
}

/** Odpowiedź w wątku (kanał wynika z wątku). Pliki — osobno (multipart, pole `files`). */
export const messageReplySchema = z.object({ body: z.string().trim().min(1, 'Wpisz treść wiadomości').max(MESSAGE_BODY_MAX) });
export type MessageReplyInput = z.infer<typeof messageReplySchema>;

/** Nowa wiadomość z kartoteki: e-mail (firma + adres) albo do strefy klienta sprawy. */
export const messageNewSchema = z.discriminatedUnion('channel', [
  z.object({
    channel: z.literal('email'),
    companyId: z.string().uuid(),
    to: z.string().trim().email('Podaj poprawny adres e-mail'),
    subject: z.string().trim().min(1, 'Wpisz temat').max(300),
    body: z.string().trim().min(1, 'Wpisz treść wiadomości').max(MESSAGE_BODY_MAX),
  }),
  z.object({
    channel: z.literal('strefa'),
    /** sprawa, której dotyczy wiadomość (null — ogólna) */
    caseId: z.string().uuid().nullable().default(null),
    body: z.string().trim().min(1, 'Wpisz treść wiadomości').max(MESSAGE_BODY_MAX),
  }),
]);
export type MessageNewInput = z.infer<typeof messageNewSchema>;

export const threadPinSchema = z.object({ caseId: z.string().uuid().nullable() });
export type ThreadPinInput = z.infer<typeof threadPinSchema>;

/** Wiadomość klienta ze strefy (bez tematu — jedna rozmowa klienta; opcjonalnie sprawa, której dotyczy). */
export const portalMessageSchema = z.object({
  body: z.string().trim().min(1, 'Wpisz treść wiadomości').max(MESSAGE_BODY_MAX),
  caseId: z.string().uuid().nullable().default(null),
});

export interface PortalMessageDto {
  id: string;
  direction: 'in' | 'out';
  /** „Pan(i)” dla klienta, imię i nazwisko pracownika dla odpowiedzi biura */
  author: string;
  body: string;
  at: string;
  attachments: { id: string; fileName: string; size: number }[];
  /** sprawa wiadomości (null — ogólna) */
  caseId: string | null;
  caseNumber: string | null;
  /** odpowiedź biura, której klient jeszcze nie widział (starsze serwery nie wysyłają) */
  unread?: boolean;
}

// ── skrzynka odbiorcza firmy (IMAP) ──

export const MAILBOX_STATUSES = ['nieskonfigurowana', 'ok', 'blad'] as const;
export type MailboxStatus = (typeof MAILBOX_STATUSES)[number];

export interface MailboxDto {
  companyId: string;
  enabled: boolean;
  host: string;
  port: number;
  tls: boolean;
  username: string;
  folder: string;
  /** hasło zapisane (wartość nigdy nie wraca z serwera) */
  passwordSet: boolean;
  passwordSetAt: string | null;
  status: MailboxStatus;
  lastCheckAt: string | null;
  lastError: string;
  /** maile przychodzące od tej chwili (wcześniejsze w skrzynce nie są pobierane) */
  importSince: string | null;
  keyConfigured: boolean;
}

export const mailboxUpdateSchema = z.object({
  enabled: z.boolean(),
  host: z
    .string()
    .trim()
    .max(253)
    .regex(/^$|^[a-zA-Z0-9.-]+$/, 'Podaj nazwę serwera, np. imap.example.com'),
  port: z.coerce.number().int().min(1).max(65535).default(993),
  tls: z.boolean().default(true),
  username: z.string().trim().max(320),
  /** puste — bez zmiany zapisanego hasła */
  password: z.string().max(500).optional(),
  folder: z.string().trim().min(1).max(200).default('INBOX'),
});
export type MailboxUpdateInput = z.infer<typeof mailboxUpdateSchema>;

/** „Importuj maile od klienta”: wynik jednego przejścia po skrzynkach firm (docs/21). */
export interface CorrespondenceImportResultDto {
  /** nowe wiadomości zapisane w kartotece */
  imported: number;
  /** wiadomości od klienta, które już są w systemie (ten sam Message-ID) */
  skipped: number;
  /** przeszukane skrzynki (firmy) */
  mailboxes: number;
  /** skrzynki, których nie udało się przeszukać (komunikat bez danych logowania) */
  errors: { company: string; message: string }[];
  /** zostały jeszcze nowe wiadomości ponad limit jednego importu — można nacisnąć ponownie */
  more: boolean;
}

// ── dokumenty klienta (wszystkie sprawy + bez sprawy) ──

export const CLIENT_DOCUMENT_KINDS = ['umowa', 'aneks', 'oferta', 'protokol', 'faktura', 'dokument', 'zdjecie', 'korespondencja'] as const;
export type ClientDocumentKind = (typeof CLIENT_DOCUMENT_KINDS)[number];
export const CLIENT_DOCUMENT_KIND_LABELS: Record<ClientDocumentKind, string> = {
  umowa: 'Umowy',
  aneks: 'Aneksy',
  oferta: 'Oferty',
  protokol: 'Protokoły',
  faktura: 'Faktury',
  dokument: 'Dokumenty',
  zdjecie: 'Zdjęcia z prac',
  korespondencja: 'Załączniki korespondencji',
};

export interface ClientDocumentDto {
  /** klucz unikalny w liście (rodzaj + id) */
  key: string;
  kind: ClientDocumentKind;
  /** nazwa do pokazania (numer dokumentu, nazwa pliku) */
  title: string;
  /** dopisek: rodzaj dokumentu sprawy, opis, stan, źródło */
  detail: string;
  fileName: string;
  /** adres pobrania w API (bez /api) */
  url: string;
  at: string;
  /** dokument sprawy / dokument bez sprawy: kategoria */
  category: CaseDocumentCategory | null;
  /** dokument bez sprawy: id do przypięcia / usunięcia */
  partyDocumentId: string | null;
  canDelete: boolean;
}

export interface ClientDocumentGroupDto {
  /** null — dokumenty bez sprawy */
  case: { id: string; number: string; title: string; closed: boolean } | null;
  documents: ClientDocumentDto[];
}

export interface ClientDocumentsDto {
  groups: ClientDocumentGroupDto[];
  /** rodzaje pominięte z braku uprawnień (np. faktury) */
  hiddenKinds: ClientDocumentKind[];
  canAdd: boolean;
  /** sprawy klienta do wyboru przy dodawaniu i przypinaniu (tylko te, w których można dodawać dokumenty) */
  cases: { id: string; number: string; title: string }[];
}

export const partyDocumentMetaSchema = z.object({
  category: z.enum(CASE_DOCUMENT_CATEGORIES).default('inne'),
  description: z.string().trim().max(500).default(''),
});

export const partyDocumentPinSchema = z.object({ caseId: z.string().uuid() });
export type PartyDocumentPinInput = z.infer<typeof partyDocumentPinSchema>;

/** Wyszukiwanie w dokumentach klienta: każde słowo musi wystąpić w tytule, opisie, nazwie pliku, rodzaju albo numerze sprawy. */
export function clientDocumentMatches(d: ClientDocumentDto, caseNumber: string | null, query: string): boolean {
  const words = query.toLocaleLowerCase('pl').split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = [d.title, d.detail, d.fileName, CLIENT_DOCUMENT_KIND_LABELS[d.kind], caseNumber ?? 'bez sprawy'].join(' ').toLocaleLowerCase('pl');
  return words.every((w) => hay.includes(w));
}

/** Temat bez przedrostków odpowiedzi / przekazania (Re:, Odp:, Fwd:, PD:, [TEST]) — do łączenia w wątki. */
export function normalizeSubject(s: string): string {
  let out = s.trim();
  for (;;) {
    const next = out.replace(/^(\[test\]\s*|(re|odp|fw|fwd|pd|aw|wg)(\[\d+\])?\s*:\s*)/i, '').trim();
    if (next === out) break;
    out = next;
  }
  return out.replace(/\s+/g, ' ').toLocaleLowerCase('pl');
}
